import React, { useContext } from "react";
import { DefaultButton, FontIcon, Spinner, SpinnerSize } from "@fluentui/react";
import { ThemeContext } from "@fluentui/react-theme-provider";
import { ThemeProvider } from "styled-components";
import DocumentTitle from "react-document-title";
import CompanyOnboardingStepper, { CompanyOnboardingStep } from "@/screen/Companies/components/CompanyOnboardingStepper";
import CompanySectionCard from "@/screen/Companies/components/CompanySectionCard";
import IdDocumentSideUpload from "@/screen/Companies/components/IdDocumentSideUpload";
import RepresentativeIdDataSection from "@/screen/Companies/CompanyRepresentative/RepresentativeIdDataSection";
import RepresentativeAdditionalDataSection from "@/screen/Companies/CompanyRepresentative/RepresentativeAdditionalDataSection";
import { useCompanyRepresentativeController } from "@/screen/Companies/CompanyRepresentative/useCompanyRepresentativeController";
import { CompanyOnboardingStrings } from "@/screen/Companies/companyOnboarding.strings";
import { Cont, MainCont, BackLink, Header, InfoBanner, TwoColumnGrid, TestActionRow, FormFooter, SubmitNotice, ErrorNotice, ResultPanel, ResultActions } from "@/screen/Companies/Styles/CompanyOnboardingStyles";

// Paso 1 del alta de empresa: alta de un representante legal con su DNI y los datos mínimos para NextPortfolio
const CompanyRepresentativeScreen: React.FC = () =>
{
    const themeContext = useContext(ThemeContext);
    const controller = useCompanyRepresentativeController();
    const {phase, formData} = controller;
    const isReview = phase === "review";

    return(
        <DocumentTitle title={`IronIA - ${CompanyOnboardingStrings.representativeNewTitle}`}>
            <Cont theme={themeContext}>
                <ThemeProvider theme={themeContext}>
                    <MainCont>
                        <BackLink type="button" onClick={controller.goToIdentities}>
                            <FontIcon iconName="ChevronLeft" />
                            {CompanyOnboardingStrings.backToIdentities}
                        </BackLink>
                        <Header>
                            <h1>{CompanyOnboardingStrings.representativeNewTitle}</h1>
                            <h2>{CompanyOnboardingStrings.representativeNewSubtitle}</h2>
                        </Header>
                        <CompanyOnboardingStepper currentStep={CompanyOnboardingStep.Representatives} />
                        <InfoBanner role="note">
                            <FontIcon iconName="Info" />
                            <span>{CompanyOnboardingStrings.representativeReuseInfo}</span>
                        </InfoBanner>

                        {phase === "done" && controller.result ?
                        (
                            <ResultPanel role="status">
                                <FontIcon iconName="CompletedSolid" />
                                <div>
                                    <b>{CompanyOnboardingStrings.representativeCreated}: {controller.result.fullName}</b>
                                    <p>{CompanyOnboardingStrings.representativeCreatedHint}</p>
                                </div>
                                <ResultActions>
                                    <DefaultButton className="button tiny-secondary-button" onClick={controller.goToIdentities}>
                                        {CompanyOnboardingStrings.backToIdentities}
                                    </DefaultButton>
                                    <DefaultButton className="button tiny-secondary-button" onClick={controller.handleAddAnother}>
                                        {CompanyOnboardingStrings.addAnotherRepresentative}
                                    </DefaultButton>
                                    <DefaultButton className="button tiny-primary-button" onClick={controller.goToCompanySubscription}>
                                        {CompanyOnboardingStrings.continueWithCompany}
                                    </DefaultButton>
                                </ResultActions>
                            </ResultPanel>
                        ) :
                        (
                            <React.Fragment>
                                <CompanySectionCard number={1} title={CompanyOnboardingStrings.representativeIdDocumentTitle} description={CompanyOnboardingStrings.representativeIdDocumentDescription}>
                                    <TwoColumnGrid>
                                        <IdDocumentSideUpload label={CompanyOnboardingStrings.idDocumentFront} file={controller.idFront} onFileChange={controller.handleIdFrontChange} />
                                        <IdDocumentSideUpload label={CompanyOnboardingStrings.idDocumentBack} file={controller.idBack} onFileChange={controller.handleIdBackChange} />
                                    </TwoColumnGrid>
                                    {(phase === "upload" || phase === "reading") &&
                                    (
                                        <TestActionRow>
                                            <DefaultButton className="button tiny-primary-button" onClick={controller.handleReadIdDocument} disabled={!controller.canReadIdDocument}>
                                                {phase === "reading" && <Spinner size={SpinnerSize.small} style={{marginRight: "8px"}} />}
                                                {phase === "reading" ? CompanyOnboardingStrings.readingIdDocument : CompanyOnboardingStrings.readIdDocument}
                                            </DefaultButton>
                                        </TestActionRow>
                                    )}
                                </CompanySectionCard>

                                {/* Persona ya existente: no se muestra ningún dato suyo (quien sube el DNI podría no ser ella) */}
                                {phase === "existing" &&
                                (
                                    <React.Fragment>
                                        {controller.existingKind === "otherUser" ?
                                        (
                                            // Interviniente de otro usuario: se pide acceso al titular (préstamo con su aprobación)
                                            <ResultPanel role={controller.accessRequested ? "status" : undefined}>
                                                <FontIcon iconName={controller.accessRequested ? "CompletedSolid" : "Contact"} />
                                                <div>
                                                    <b>{controller.accessRequested ? CompanyOnboardingStrings.accessRequested : CompanyOnboardingStrings.representativeOwnedByOtherUser}</b>
                                                    <p>{controller.accessRequested ? CompanyOnboardingStrings.accessRequestedHint : CompanyOnboardingStrings.representativeOwnedByOtherUserHint}</p>
                                                </div>
                                                <ResultActions>
                                                    <DefaultButton className="button tiny-secondary-button" onClick={controller.handleAddAnother} disabled={controller.isRequestingAccess}>
                                                        {CompanyOnboardingStrings.addAnotherRepresentative}
                                                    </DefaultButton>
                                                    {!controller.accessRequested &&
                                                    (
                                                        <DefaultButton className="button tiny-primary-button" onClick={controller.handleRequestAccess} disabled={controller.isRequestingAccess}>
                                                            {controller.isRequestingAccess && <Spinner size={SpinnerSize.small} style={{marginRight: "8px"}} />}
                                                            {controller.isRequestingAccess ? CompanyOnboardingStrings.requestingAccess : CompanyOnboardingStrings.requestAccess}
                                                        </DefaultButton>
                                                    )}
                                                </ResultActions>
                                            </ResultPanel>
                                        ) :
                                        (
                                            <ErrorNotice role="alert">
                                                {controller.existingKind === "diaphanumClient" ?
                                                (
                                                    <React.Fragment>
                                                        <b>{CompanyOnboardingStrings.representativeIsDiaphanumClient}.</b>
                                                        <p>{CompanyOnboardingStrings.representativeIsDiaphanumClientHint}</p>
                                                    </React.Fragment>
                                                ) :
                                                (
                                                    <React.Fragment>
                                                        <b>{CompanyOnboardingStrings.representativeAlreadyExists}.</b>
                                                        <p>{CompanyOnboardingStrings.representativeAlreadyExistsHint}</p>
                                                    </React.Fragment>
                                                )}
                                            </ErrorNotice>
                                        )}
                                        {controller.accessRequestErrors.length > 0 &&
                                        (
                                            <ErrorNotice role="alert">
                                                {controller.accessRequestErrors.map((message, index) => <p key={index}>{message}</p>)}
                                            </ErrorNotice>
                                        )}
                                    </React.Fragment>
                                )}

                                {isReview &&
                                (
                                    <React.Fragment>
                                        <RepresentativeIdDataSection formData={formData} readingWarning={controller.readingWarning}
                                            onChange={controller.handleChange} onPermanentIdExpirationChange={controller.handlePermanentIdExpirationChange}
                                        />
                                        <RepresentativeAdditionalDataSection formData={formData} onChange={controller.handleChange} />
                                        {controller.submitErrors.length > 0 &&
                                        (
                                            <ErrorNotice role="alert">
                                                {controller.submitErrors.map((message, index) => <p key={index}>{message}</p>)}
                                            </ErrorNotice>
                                        )}
                                        {!controller.isSubmitting && controller.missingFieldLabels.length > 0 &&
                                        (
                                            <SubmitNotice role="status">
                                                {CompanyOnboardingStrings.representativeMissingFields} {controller.missingFieldLabels.join(", ")}.
                                            </SubmitNotice>
                                        )}
                                        <FormFooter>
                                            <DefaultButton className="button tiny-secondary-button" onClick={controller.goToIdentities} disabled={controller.isSubmitting}>
                                                {CompanyOnboardingStrings.cancel}
                                            </DefaultButton>
                                            <DefaultButton className="button tiny-primary-button" onClick={controller.handleSubmit} disabled={!controller.canSubmit}>
                                                {controller.isSubmitting && <Spinner size={SpinnerSize.small} style={{marginRight: "8px"}} />}
                                                {controller.isSubmitting ? CompanyOnboardingStrings.representativeSubmitting : CompanyOnboardingStrings.representativeSubmit}
                                            </DefaultButton>
                                        </FormFooter>
                                        {controller.isSubmitting && <SubmitNotice role="status">{CompanyOnboardingStrings.representativeSubmitting}</SubmitNotice>}
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        )}
                    </MainCont>
                </ThemeProvider>
            </Cont>
        </DocumentTitle>
    );
};

export default CompanyRepresentativeScreen;
